import React from "react";
import {
  AiOutlineAlert,
  AiOutlineBook,
  AiOutlineCalendar,
  AiOutlineDollarCircle,
  AiOutlineInbox,
  AiOutlineTeam,
} from "react-icons/ai";
import { HiOutlineAcademicCap, HiOutlineOfficeBuilding, HiOutlineUserGroup } from "react-icons/hi";
import { IoShieldCheckmarkOutline } from "react-icons/io5";
import { useLocation, useNavigate } from "react-router-dom";
import useGroupProgramTypes from "./useGroupProgramTypes";

/**
 * The group admin's Menu — same visual language as AdminMenu.js (same tone
 * palette, same category-grouped tile grid, same hover/active styling) so it
 * reads as part of the same product rather than a bolted-on dashboard. Only
 * carries tiles for what the Group Console actually has data for; there's no
 * "School Setting" tile here since there's no single school to configure at
 * group level.
 */
const TONES = {
  Students: { card: "#d1fae5", circle: "#6ee7b7", icon: "#065f46" },
  Instructors: { card: "#dbeafe", circle: "#93c5fd", icon: "#1d4ed8" },
  Parents: { card: "#f3e8ff", circle: "#d8b4fe", icon: "#7e22ce" },
  Admins: { card: "#fce7f3", circle: "#f9a8d4", icon: "#be185d" },
  Admissions: { card: "#ede9fe", circle: "#c4b5fd", icon: "#6d28d9" },
  Fee: { card: "#dcfce7", circle: "#86efac", icon: "#15803d" },
  Expenses: { card: "#fff1f2", circle: "#fecdd3", icon: "#9f1239" },
  "Stock Inventory": { card: "#f7fee7", circle: "#d9f99d", icon: "#4d7c0f" },
  "Wake-up Alarm": { card: "#ffedd5", circle: "#fdba74", icon: "#c2410c" },
  Schedule: { card: "#e0f2fe", circle: "#7dd3fc", icon: "#0369a1" },
  "Senior Course Board": { card: "#f0fdfa", circle: "#5eead4", icon: "#0f766e" },
  "Junior Course Board": { card: "#fef9c3", circle: "#fde047", icon: "#a16207" },
};

const CATEGORIES = [
  { label: "People", items: ["Students", "Instructors", "Parents", "Admins"] },
  { label: "Admissions", items: ["Admissions"] },
  {
    label: "Academic",
    items: ["Senior Course Board", "Junior Course Board"],
  },
  { label: "Finance", items: ["Fee", "Expenses"] },
  { label: "Operations", items: ["Stock Inventory", "Wake-up Alarm", "Schedule"] },
];

const GroupConsoleMenu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasSenior, hasJunior } = useGroupProgramTypes();

  const allItems = [
    {
      title: "Students",
      icon: <HiOutlineAcademicCap size={28} />,
      url: "/dashboard/group/menu/students",
    },
    {
      title: "Instructors",
      icon: <AiOutlineTeam size={28} />,
      url: "/dashboard/group/menu/instructors",
    },
    {
      title: "Parents",
      icon: <HiOutlineUserGroup size={28} />,
      url: "/dashboard/group/menu/parents",
    },
    {
      title: "Admins",
      icon: <IoShieldCheckmarkOutline size={28} />,
      url: "/dashboard/group/menu/admins",
    },
    {
      title: "Admissions",
      icon: <HiOutlineOfficeBuilding size={28} />,
      url: "/dashboard/group/menu/admissions",
    },
    {
      title: "Fee",
      icon: <AiOutlineDollarCircle size={28} />,
      url: "/dashboard/group/menu/fee",
    },
    {
      title: "Expenses",
      icon: <AiOutlineDollarCircle size={28} />,
      url: "/dashboard/group/menu/expenses",
    },
    {
      title: "Stock Inventory",
      icon: <AiOutlineInbox size={28} />,
      url: "/dashboard/group/menu/stock",
    },
    {
      title: "Wake-up Alarm",
      icon: <AiOutlineAlert size={28} />,
      url: "/dashboard/group/menu/alarm",
    },
    {
      title: "Schedule",
      icon: <AiOutlineCalendar size={28} />,
      url: "/dashboard/group/menu/schedule",
    },
    // A branch is exclusively Senior or Junior/Montessori — never both — so
    // whether the GROUP shows one tile, the other, or both depends on
    // whether any of its branches are each type.
    ...(hasSenior
      ? [
          {
            title: "Senior Course Board",
            icon: <AiOutlineBook size={28} />,
            url: "/dashboard/group/menu/course-board/senior",
          },
        ]
      : []),
    ...(hasJunior
      ? [
          {
            title: "Junior Course Board",
            icon: <AiOutlineBook size={28} />,
            url: "/dashboard/group/menu/course-board/junior",
          },
        ]
      : []),
  ];

  return (
    <div className="p-8">
      <div className="bg-white p-8 rounded-2xl">
        <div style={{ marginBottom: 24 }}>
          <div className="text-sm text-gray-500">
            Read-only across every branch in your group. Click a branch name
            inside any screen to switch into it and manage things there.
          </div>
        </div>
        {CATEGORIES.map(({ label, items: titles }) => {
          const catItems = allItems.filter((i) => titles.includes(i.title));
          if (!catItems.length) return null;
          return (
            <div key={label} style={{ marginBottom: 32 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#94a3b8",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  marginBottom: 14,
                  paddingBottom: 8,
                  borderBottom: "1px solid #f1f5f9",
                }}
              >
                {label}
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: 16,
                }}
              >
                {catItems.map((item) => {
                  const tone = TONES[item.title] || {
                    card: "#f1f5f9",
                    circle: "#cbd5e1",
                    icon: "#475569",
                  };
                  const isActive = location.pathname.startsWith(item.url);
                  return (
                    <div
                      key={item.title}
                      onClick={() => navigate(item.url)}
                      style={{
                        background: tone.card,
                        borderRadius: 20,
                        padding: "20px 12px 16px",
                        cursor: "pointer",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 12,
                        boxShadow: isActive
                          ? `0 0 0 2.5px ${tone.icon}`
                          : "0 2px 8px rgba(0,0,0,0.06)",
                        transition: "all 0.15s ease",
                        minHeight: 120,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-3px)";
                        e.currentTarget.style.boxShadow =
                          "0 8px 20px rgba(0,0,0,0.11)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = isActive
                          ? `0 0 0 2.5px ${tone.icon}`
                          : "0 2px 8px rgba(0,0,0,0.06)";
                      }}
                    >
                      <div
                        style={{
                          width: 58,
                          height: 58,
                          borderRadius: "50%",
                          background: tone.circle,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: tone.icon,
                          flexShrink: 0,
                        }}
                      >
                        {item.icon}
                      </div>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          textAlign: "center",
                          lineHeight: 1.35,
                          color: "#1e3a5f",
                        }}
                      >
                        {item.title}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GroupConsoleMenu;
